import {verifiserErIkkeNil, verifiserErUsann} from "felles-ng2/støttefunksjoner/antakelser";
import {EntitetLager, EntitetMedId, MedId} from "felles-ng2/tjenester/datalager/datatjeneste";
import {deleteEntities, upsertEntities} from "@ngneat/elf-entities";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {EnEllerFlere, ModellHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellHendelseHåndteringBygger";
import {firstValueFrom, Observable} from "rxjs";
import {HttpErrorResponse, HttpResponse} from "@angular/common/http";
import {HttpResponskoder} from "felles-ng2/infrastruktur/http/httpResponskoder";
import {erIkkeTilgangTilEntitetException} from "felles-ng2/infrastruktur/http/httpErrorResponseTyper";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import {somListe} from "felles-ng2/støttefunksjoner/lister";

const debug = initDebugger('ModellOpprettetEllerOppdatertHendelseHåndtering');

/** Håndtering av modelloppdatering-push meldinger for opprett/oppdater hendelser */
export class ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, ENTITET_TYPE extends EntitetMedId<IdFelt>, IdFelt extends string = 'id'> extends ModellHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, ENTITET_TYPE, IdFelt>> {
    private _entitetLaster!: (hendelseModell: HENDELSE_MODELL_TYPE) => Observable<HttpResponse<EnEllerFlere<ENTITET_TYPE>>>;
    private _muterEntitetFn: ((entitet:ENTITET_TYPE) => ENTITET_TYPE) = entitet => entitet;

    constructor(datalager: EntitetLager<ENTITET_TYPE,IdFelt>, modelloppdateringer: Modelloppdateringer) {
        super(datalager, modelloppdateringer, debug);
    }

    medEntitetLaster(entitetLaster: (hendelseModell: HENDELSE_MODELL_TYPE) => Observable<HttpResponse<EnEllerFlere<ENTITET_TYPE>>>): ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, ENTITET_TYPE, IdFelt> {
        this._entitetLaster = entitetLaster;
        return this;
    }

    get entitetLaster(): (hendelseModell: HENDELSE_MODELL_TYPE) => Observable<HttpResponse<EnEllerFlere<ENTITET_TYPE>>> {
        return this._entitetLaster;
    }

    medForberedLageroppdatering(muterEntitetFn: (entitet:ENTITET_TYPE) => ENTITET_TYPE) {
        this._muterEntitetFn = muterEntitetFn;
        return this;
    }

    get forberedLageroppdatering(){
        return this._muterEntitetFn;
    }

    //ModellHendelseHåndtering impl

    protected opprettKopi(): ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, ENTITET_TYPE, IdFelt> {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, ENTITET_TYPE, IdFelt>(this.datalager, this.modelloppdateringer)
          .medEntitetLaster(this._entitetLaster)
          .medForberedLageroppdatering(this.forberedLageroppdatering);
    }

    /** Laster entitet(er) representert via angitt hendelseModell og oppdaterer datalager med denne.
     * Dersom lasting feiler med en IkkeTilgangTilEntitetException, GONE eller NOT_FOUND status fjernes entitet fra datalager. */
    protected async oppdaterLager(hendelseModell: HENDELSE_MODELL_TYPE) {
        verifiserErIkkeNil(this.entitetLaster, 'har du glemt å kalle medEntitetLaster(..)?');
        verifiserErUsann(this.ignorerAbonnentSjekk, 'ignorerAbonnentSjekk støttes ikke');

        try {
            const respons = await firstValueFrom(this.entitetLaster(hendelseModell));
            if (respons.status != HttpResponskoder.OK) return;
            this.håndterRespons(respons.body!, hendelseModell);

        } catch (e) {
            if (!(e instanceof HttpErrorResponse)) throw e;
            this.håndterFeiletEntitetLasting(e, hendelseModell);
        }
    }

    // private hjelpemetoder

    private håndterRespons(entitet: EnEllerFlere<ENTITET_TYPE>, hendelseModell: HENDELSE_MODELL_TYPE) {
        const oppdatertEntitet= somListe(entitet).map(e => this.forberedLageroppdatering(e)) ;
        this.debug('oppdaterer lager', hendelseModell, oppdatertEntitet);
        this.datalager.update(upsertEntities(oppdatertEntitet));
    }

    private håndterFeiletEntitetLasting(respons: HttpErrorResponse, hendelseModell: HENDELSE_MODELL_TYPE) {
        if (erIkkeTilgangTilEntitetException(respons.status)
          || respons.status == HttpResponskoder.NOT_FOUND
          || respons.status == HttpResponskoder.GONE) {
            this.fjernFraDatalager(hendelseModell, respons.status)
            return;
        }
        this.debug(`lasting av entitet ${this.entitetId(hendelseModell)} feilet med responskode ${respons.status}`, hendelseModell);
    }

    private fjernFraDatalager(hendelseModell: HENDELSE_MODELL_TYPE, responskode: number) {
        const entitetId = this.entitetId(hendelseModell);
        this.debug(`lasting av entitet ${this.entitetId(hendelseModell)} feilet med responskode ${responskode}, fjerner entitet fra datalager`, hendelseModell);
        this.datalager.update(deleteEntities(entitetId));
    }

}
